import type { Editor } from "ckeditor5"
import { createMarkerValue, getObjectFitFromModel } from "./utils"

export function setupConversions(editor: Editor): void {
  // Register objectFit attribute on image schema
  const schema = (editor.model as any).schema
  if (schema) {
    schema.extend("imageBlock", { allowAttributes: ["objectFit"] })
    schema.extend("imageInline", { allowAttributes: ["objectFit"] })
  }

  // PostFixer: Copy objectFit from paragraph marker to image children
  editor.model.document.registerPostFixer((writer: any) => {
    const differ = editor.model.document.differ
    let changed = false

    for (const change of differ.getChanges()) {
      if (change.type === "insert" && change.position) {
        const element = change.position.parent
        if (element && element.name === "paragraph") {
          const marker = element.getAttribute("objectFit")
          if (marker && typeof marker === "string" && marker.startsWith("__img:")) {
            const objectFit = marker.replace("__img:", "")
            for (const child of element.getChildren()) {
              if ((child as any).name === "imageInline" || (child as any).name === "imageBlock") {
                writer.setAttribute("objectFit", objectFit, child)
              }
            }
            writer.removeAttribute("objectFit", element)
            changed = true
          }
        }
      }
    }

    return changed
  })

  // Upcast: Read data-object-fit from img and set marker on paragraph
  editor.conversion.for("upcast").add((dispatcher: any) => {
    dispatcher.on("element:img", (evt: any, data: any) => {
      const viewElement = data.viewItem
      if (!viewElement) return

      const objectFit = viewElement.getAttribute("data-object-fit")
      if (objectFit) {
        const modelElement = data.modelCursor?.parent
        if (modelElement && "getAttribute" in modelElement) {
          editor.model.change((writer: any) => {
            writer.setAttribute("objectFit", createMarkerValue(objectFit), modelElement)
          })
        }
      }
    })
  })

  // Downcast: Intercept getData to inject data-object-fit
  const originalGetData = editor.getData.bind(editor)
  editor.getData = () => {
    const html = originalGetData()
    return injectObjectFitToData(html, editor)
  }
}

function injectObjectFitToData(html: string, editor: Editor): string {
  if (typeof window === "undefined") return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")

  const processImage = (img: HTMLImageElement) => {
    const src = img.getAttribute("src")
    if (!src) return

    const objectFit = getObjectFitFromModel(editor, src)
    if (objectFit) {
      img.setAttribute("data-object-fit", objectFit)
      const currentStyle = img.getAttribute("style") || ""
      const styleWithout = currentStyle.replace(/object-fit\s*:\s*[^;]+;?/gi, "").trim()
      const newStyle = styleWithout
        ? `${styleWithout};object-fit:${objectFit}`
        : `object-fit:${objectFit}`
      img.setAttribute("style", newStyle)
    }
  }

  doc.querySelectorAll("figure img").forEach((el) => processImage(el as HTMLImageElement))
  doc.querySelectorAll("img:not(figure img)").forEach((el) => processImage(el as HTMLImageElement))

  return doc.body.innerHTML
}
